import { useState } from "react";
import { Drawer, Box, IconButton, Typography, Divider, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { colors } from "../theme";

// clipboard helper with HTTP fallback (navigator.clipboard requires HTTPS)
const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }
  return fallbackCopy(text);
};

const fallbackCopy = (text) => {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    ta.style.top = "0";
    ta.style.opacity = "0";
    // MUI Drawer/Modal traps focus — append inside the active modal container,
    // otherwise the selection is rejected and execCommand("copy") silently fails.
    const container =
      document.activeElement?.closest?.('[role="dialog"], [role="presentation"], .MuiDrawer-root') ||
      document.body;
    container.appendChild(ta);
    const selection = document.getSelection();
    const prevRange = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const range = document.createRange();
    range.selectNodeContents(ta);
    selection.removeAllRanges();
    selection.addRange(range);
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    container.removeChild(ta);
    if (prevRange) { selection.removeAllRanges(); selection.addRange(prevRange); }
    return ok ? Promise.resolve() : Promise.reject(new Error("execCommand returned false"));
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * Reusable right-anchored side panel (drawer).
 *
 * Props
 * - open:        boolean
 * - onClose:     () => void
 * - title:       string | node           — main title
 * - subtitle:    string | node           — small caption under the title
 * - icon:        node                    — icon shown left of the title
 * - actions:     node                    — extra header actions (buttons) shown right of close
 * - width:       number | string         — drawer width (default 520)
 * - children:    panel body content
 */
const SidePanel = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  actions,
  width = 520,
  children,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above MUI's default Modal z-index (1300): this panel is also opened from
      // inside other Dialogs (e.g. the Traffic Logs pair-detail popup), and a plain
      // Drawer's default z-index (1200) would otherwise render it behind that Dialog.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: width },
          maxWidth: "100vw",
          backgroundColor: "#161616",
          backgroundImage: "none",
          color: colors.grey[100],
          borderLeft: `1px solid ${colors.grey[800]}`,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.6)",
        },
      }}
    >
      {/* ── Header ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          px: 2.5,
          py: 1.8,
          borderBottom: `1px solid ${colors.grey[800]}`,
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "#1e1e1e",
        }}
      >
        {icon && (
          <Box sx={{ display: "flex", alignItems: "center", color: "#459095" }}>
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && (
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: colors.grey[100],
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{
                fontSize: 11,
                color: colors.grey[400],
                mt: 0.3,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: colors.grey[300] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Body ─────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>{children}</Box>
    </Drawer>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Small layout helpers — reusable inside any SidePanel body
 * ────────────────────────────────────────────────────────────────── */

export const SidePanelSection = ({ title, action, children, dense = false }) => (
  <Box sx={{ mb: dense ? 1.5 : 2.5 }}>
    {(title || action) && (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        {title && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#459095",
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            {title}
          </Typography>
        )}
        {action}
      </Box>
    )}
    <Box>{children}</Box>
    <Divider sx={{ mt: dense ? 1.2 : 2, borderColor: `${colors.grey[700]}55` }} />
  </Box>
);

/**
 * Elevated "stat tile" grid — visually distinct from the plain SidePanelKV rows (a bordered
 * card instead of dashed-underline rows, same treatment as the Endpoints source/destination
 * blocks), for a small set of closely-related numeric values (e.g. a statistical summary) that
 * read better scanned at a glance than as a vertical list. `children` renders extra content
 * (e.g. a mini distribution indicator) inside the same card.
 */
export const SidePanelStatGrid = ({ title, tiles = [], children }) => {
  const visible = tiles.filter((t) => t.value != null && t.value !== "");
  if (!visible.length) return null;
  return (
    <Box sx={{ mb: 2.5 }}>
      {title && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ borderRadius: 1, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, p: 1.5 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 0.5 }}>
          {visible.map(({ label, value, color }) => (
            <Box key={label} sx={{ textAlign: "center", px: 0.5 }}>
              <Typography sx={{ fontSize: 9.5, color: colors.grey[400], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3, whiteSpace: "nowrap" }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", color: color ?? colors.grey[100] }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        {children && <Box sx={{ mt: 2 }}>{children}</Box>}
      </Box>
      <Divider sx={{ mt: 2, borderColor: `${colors.grey[700]}55` }} />
    </Box>
  );
};

export const SidePanelKV = ({ label, value, mono = false, copyable = false, icon = null, action = null }) => {
  const [copied, setCopied] = useState(false);
  if (value == null || value === "") return null;
  const isNode = value && typeof value === "object" && (value.$$typeof || Array.isArray(value));
  const display = isNode ? value : String(value);
  const canCopy = copyable && !isNode;

  const handleCopy = (e) => {
    e.stopPropagation();
    copyToClipboard(display).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(90px, 120px) 1fr",
        gap: 1.2,
        py: 0.55,
        borderBottom: `1px dashed ${colors.grey[800]}`,
        "&:last-of-type": { borderBottom: "none" },
        alignItems: "start",
      }}
    >
      <Typography
        sx={{
          fontSize: 11.5,
          color: colors.grey[400],
          textTransform: "uppercase",
          letterSpacing: 0.4,
          fontWeight: 500,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: action ? "space-between" : "flex-start",
          gap: 0.8,
          fontSize: 12.5,
          "& .copy-btn": { opacity: 0, transition: "opacity 120ms" },
          "&:hover .copy-btn": { opacity: 1 },
        }}
      >
        <Box
          onClick={canCopy ? handleCopy : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            minWidth: 0,
            fontFamily: mono ? "monospace" : undefined,
            color: colors.grey[100],
            wordBreak: "break-word",
            cursor: canCopy ? "pointer" : "default",
            "&:hover": canCopy ? { color: "#459095" } : undefined,
          }}
        >
          {icon && <Box sx={{ display: "flex", alignItems: "center", color: "#459095" }}>{icon}</Box>}
          {isNode ? display : <span>{display}</span>}
          {canCopy && (
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton
                size="small"
                className="copy-btn"
                onClick={handleCopy}
                sx={{
                  p: 0.3,
                  ml: 0.3,
                  color: copied ? "#4cceac" : "#459095",
                  opacity: copied ? "1 !important" : undefined,
                }}
              >
                {copied
                  ? <CheckIcon sx={{ fontSize: 14 }} />
                  : <ContentCopyIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {action && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 1 }}
          >
            {action}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SidePanel;
